import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCircle, KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async () => {
    if (next.length < 12) return toast.error("New password must be at least 12 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    if (next === current) return toast.error("New password must differ from the current one");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password changed. Other sessions have been signed out.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 col-span-1 md:col-span-3">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="w-4 h-4 text-hocp-textMuted"/>
        <div className="text-[10px] uppercase tracking-[0.15em] text-hocp-textMuted mono">Password</div>
      </div>
      <div className="text-sm text-hocp-textPrimary mb-4">
        Change your password. All your other active sessions will be signed out.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl">
        <div>
          <Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Current password</Label>
          <Input
            type={show ? "text" : "password"}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="bg-hocp-bgCard border-hocp-border mt-1.5 mono"
            data-testid="pwd-current-input"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-hocp-textMuted">New password (≥ 12)</Label>
          <Input
            type={show ? "text" : "password"}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="bg-hocp-bgCard border-hocp-border mt-1.5 mono"
            data-testid="pwd-new-input"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Confirm new password</Label>
          <Input
            type={show ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="bg-hocp-bgCard border-hocp-border mt-1.5 mono"
            data-testid="pwd-confirm-input"
          />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <Button
          onClick={submit}
          disabled={busy || !current || !next || !confirm}
          className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-9 px-4 text-[13px]"
          data-testid="pwd-submit"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>}
          Change password
        </Button>
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="text-[12px] text-hocp-textMuted hover:text-hocp-deepTeal flex items-center gap-1"
          data-testid="pwd-toggle-visibility"
        >
          {show ? <EyeOff className="w-3.5 h-3.5"/> : <Eye className="w-3.5 h-3.5"/>}
          {show ? "Hide" : "Show"}
        </button>
      </div>
    </Card>
  );
}

export default function Profile() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <div data-testid="profile-page">
      <PageHeader title="Profile" subtitle="Your identity"/>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-6 col-span-1 flex flex-col items-center text-center">
          <div className="w-20 h-20 rounded-full bg-hocp-bgSubtle border border-hocp-border flex items-center justify-center mb-4">
            <UserCircle className="w-10 h-10 text-hocp-textMuted" strokeWidth={1.25}/>
          </div>
          <div className="font-heading text-lg text-white">{user.name}</div>
          <div className="mono text-[11px] text-hocp-textMuted mt-1">{user.email}</div>
          <div className="mt-3"><Badge tone="info">{user.role}</Badge></div>
        </Card>
        <Card className="p-6 col-span-2">
          <div className="text-[10px] uppercase tracking-[0.15em] text-hocp-textMuted mono mb-3">Resolved Permissions</div>
          <div className="flex flex-wrap gap-1.5 max-h-[400px] overflow-y-auto">
            {(user.permissions || []).map((p) => (
              <span key={p} className="mono text-[11px] bg-hocp-bgCard border border-hocp-border px-2 py-1 rounded text-hocp-textMuted">{p}</span>
            ))}
            {(!user.permissions || user.permissions.length === 0) && <span className="text-xs text-hocp-textMuted">No permissions resolved.</span>}
          </div>
        </Card>
        <ChangePasswordCard />
      </div>
    </div>
  );
}
